import { showDeleteConfirmation } from '@modules/common';
import { switchMap, map } from 'rxjs/operators';
import { BaseBloc } from '@core/utils/bloc';
import { Repository } from '@core/utils/repository';
import { handleMessage, requestMessage } from '@business/messages';
import { ChannelId, SupportChannelResponse, SupportChannelRequest, CompactChannelResponse } from '@business/entities/SupportChannel';
import { SupportChannelAPI } from '@business/api/SupportChannel';
import { R } from '@core/utils/r';

export class SupportChannelBloc extends BaseBloc {
  private readonly supportChannelRepo = new Repository<CompactChannelResponse>({
    getItemId: channel => channel.id,
  });

  private readonly channelDetailRepo = new Repository<SupportChannelResponse>({
    getItemId: channel => channel.id,
  });

  readonly channels$ = this.supportChannelRepo.items$.pipe(map(items => R.sortBy(items, ['menuOrder', 'id'])));
  readonly dChannels$ = this.channelDetailRepo.items$.pipe(map(items => R.sortBy(items, ['menuOrder', 'id'])));
  readonly selectChannel = this.supportChannelRepo.selectItem;

  onReset() {
    this.supportChannelRepo.reset();
  }

  fetchChannels = () => {
    return SupportChannelAPI.fetchChannels().pipe(
      this.supportChannelRepo.ops.upsertMany(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_channels_error') }),
    );
  };

  fetchDetailChannels = () => {
    return SupportChannelAPI.fetchDetailChannels().pipe(
      this.channelDetailRepo.ops.upsertMany(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_channels_error') }),
    );
  };

  fetchChannel = (id: ChannelId) => {
    return SupportChannelAPI.fetchChannel(id).pipe(
      this.supportChannelRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_channels_error') }),
    );
  };

  createChannel = (checklist: SupportChannelRequest) => {
    return SupportChannelAPI.createChannel(checklist).pipe(
      this.supportChannelRepo.ops.addOne(item => item),
      handleMessage({
        type: requestMessage('create_channel'),
      }),
    );
  };

  updateChannel = (id: ChannelId, data: SupportChannelRequest) => {
    return SupportChannelAPI.updateChannel(id, data).pipe(
      this.supportChannelRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_channel'),
      }),
    );
  };

  deleteChannel = (id: ChannelId) => {
    return showDeleteConfirmation('Delete Channel', 'Do you really want to remove this channel?').pipe(
      switchMap(() => SupportChannelAPI.deleteChannel(id)),
      this.supportChannelRepo.ops.removeOne(() => id),
      handleMessage({
        type: requestMessage('delete_channel'),
      }),
    );
  };

  sortChannels = (ids: ChannelId[]) => {
    return SupportChannelAPI.sortChannels(ids).pipe(
      this.supportChannelRepo.ops.upsertMany(item => ({ item })),
      handleMessage({
        error: requestMessage('update_template_section_sort'),
      }),
    );
  };
}
